import { FC, useContext, useRef, useState } from "react";
import { EDocumentCategory, TDocument } from "../../content/types";
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { UserProgramContext } from "../../context/user-program/user-program";
import { uploadDocument } from "../../api/documents";
import { assertNonNullable } from "../../utils/asserts";
import { ContainedButton } from "../styled/contained-button";
import { formatTimestamp } from "../../utils/date";
import { DOCUMENTS_CATEGORIES, MAX_UPLOAD_FILE_SIZE } from "../constants";

type Props = {
  category:
    | EDocumentCategory.DIET
    | EDocumentCategory.MOVEMENT
    | EDocumentCategory.NUTRITION;
  sectionTitle: string;
};

export const StandardDocumentsWidget: FC<Props> = ({
  category,
  sectionTitle,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { program, dispatch } = useContext(UserProgramContext);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState<string>("");

  assertNonNullable(program);

  const documents = program.sections
    .find((section) => section.title === sectionTitle)
    ?.documents.filter(
      (document) => document.category === category
    ) as Array<TDocument>;

  const uploadFile = (file: File) => {
    setUploadLoading(true);

    uploadDocument({
      title: file.name,
      file,
      sectionTitle,
      category,
    })
      .then(() => {
        dispatch({
          type: "UPLOAD_DOCUMENT",
          payload: {
            sectionTitle,
            document: {
              title: file.name,
              category,
              createdOn: new Date().getTime(),
            },
          },
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 413) {
          const message = "Die Datei, die Sie hochladen möchten, ist zu groß";
          setError(message);
          console.error(message);
        }
      })
      .finally(() => {
        setUploadLoading(false);
        fileInputRef.current?.value && (fileInputRef.current.value = "");
      });
  };

  assertNonNullable(DOCUMENTS_CATEGORIES[category]);

  return (
    <Card>
      <CardContent>
        <Box mb={3}>
          <Typography variant="h6" component="div">
            {DOCUMENTS_CATEGORIES[category].title} hochladen
          </Typography>
        </Box>
        <TableContainer component={Paper}>
          <Table aria-label={`${category} documents table`}>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {DOCUMENTS_CATEGORIES[category].title}
                </TableCell>
                <TableCell align="center">Hochgeladen am</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.createdOn}>
                  <TableCell scope="row">{document.title}</TableCell>
                  <TableCell align="right">
                    {formatTimestamp(document.createdOn)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {error && <Alert severity="error">{error}</Alert>}
      </CardContent>
      <CardActions>
        <Box display="flex" width="100%" p={2}>
          <label htmlFor={`${category}-upload-doc`}>
            <input
              ref={fileInputRef}
              disabled={uploadLoading}
              style={{ display: "none" }}
              id={`${category}-upload-doc`}
              name="upload-doc"
              type="file"
              multiple={false}
              onChange={(e) => {
                setError("");

                if (e.target.files) {
                  const file = e.target.files[0];

                  if (file.size > MAX_UPLOAD_FILE_SIZE) {
                    setError(
                      "Die Datei ist zu groß. Die maximale Dateigröße beträgt 100 MB"
                    );
                    return;
                  }

                  uploadFile(file);
                }
              }}
            />
            <ContainedButton
              muiType="normal"
              component="span"
              disabled={uploadLoading}
            >
              Neue Datei Hochladen
            </ContainedButton>
          </label>
        </Box>
      </CardActions>
    </Card>
  );
};
